import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { Message, Session } from 'talkjs/all';
import useIsInteragencyChatEnabled from '@rsos/hooks/useIsInteragencyChatEnabled';
import { playTone } from '@rsos/reduxDataStore/src/features/audioNotifications/audioHelpers';
import {
  selectCapstoneAudioSettingsForFeature,
  selectUserAudioSettingsForFeature,
} from '@rsos/sinatra/src/selectors/selectAudioSettings';
import { useMutedInteragencyConversationIds } from '../reduxDataStore/interagency/interagencyChatSubscriptions';
import { useSelectedInteragencyChat } from '../reduxDataStore/interagency/interagencySlice';
import { INTERAGENCY_CONVERSATION_PREFIX } from './useInteragencyChatToastNotifications';

interface UseInteragencyChatAudioNotificationsArgs {
  // Narrowed so tests pass a plain object rather than a whole Session.
  session: Pick<Session, 'onMessage'> | null;
  isAgencyChatTabActive: boolean;
}

/**
 * Audio counterpart of useInteragencyChatToastNotifications: plays a tone on a
 * new message in any interagency conversation the user has not muted. Shares the
 * banner's event and gates; the admin control here is Sound Notifications, not
 * Banners.
 */
function useInteragencyChatAudioNotifications({
  session,
  isAgencyChatTabActive,
}: UseInteragencyChatAudioNotificationsArgs): void {
  const isChatEnabled = useIsInteragencyChatEnabled();
  const mutedConversationIds = useMutedInteragencyConversationIds();
  const selectedInteragencyChat = useSelectedInteragencyChat();

  // sound_on/tone fall back to defaults until the admin settings UI ships.
  const { sound_on: soundOn, tone } = useSelector(
    selectCapstoneAudioSettingsForFeature('interagency_chat'),
  );
  const { volume } = useSelector(
    selectUserAudioSettingsForFeature('interagency_chat'),
  );

  const handleMessage = useCallback(
    (message: Message) => {
      const conversationId = message.conversation.id;
      if (!conversationId.startsWith(INTERAGENCY_CONVERSATION_PREFIX)) return;
      if (!soundOn) return;
      if (mutedConversationIds.includes(conversationId)) return;
      if (message.isByMe) return;
      // Muted via the speaker icon.
      if (!volume) return;
      // Already reading it — the message lands in the open chatbox.
      if (isAgencyChatTabActive && selectedInteragencyChat === conversationId) {
        return;
      }

      playTone(tone, volume);
    },
    [
      soundOn,
      tone,
      volume,
      mutedConversationIds,
      isAgencyChatTabActive,
      selectedInteragencyChat,
    ],
  );

  useEffect(() => {
    if (!session || !isChatEnabled) return;
    const subscription = session.onMessage(handleMessage);
    return () => subscription.unsubscribe();
  }, [session, isChatEnabled, handleMessage]);
}

export default useInteragencyChatAudioNotifications;
